import React, { useMemo } from 'react';

/**
 * StickyBottomMarquee — v3.4
 *
 * A thin sticky horizontal marquee strip pinned to the bottom of the browser
 * viewport. Renders an infinite seamless horizontal scroll of N items separated
 * by orange diamond glyphs. The FIRST item renders in brand-orange; remaining
 * items render in white. Items are repeated `REPEATS` times inside a single
 * track so the `translateX(0 → -1/REPEATS%)` keyframe wraps without a visible
 * jump on any viewport up to ~4100 px wide.
 *
 * Why 4× repetition?
 *   One items-iteration at desktop font ≈ 1377 px. The 2× duplication used
 *   previously produced visible black gaps on viewports wider than ~1378 px
 *   because half-track width < viewport width broke the seamless-loop math.
 *   4 repetitions → track ≈ 5508 px, comfortably exceeding 3440 px ultrawide.
 *   The keyframe shifts by exactly -25% of the track per cycle = one
 *   items-iteration — preserving the original scroll pace (40 s desktop /
 *   30 s mobile via the existing media query).
 *
 * Animation timing:
 *   • Desktop / tablet: 40s linear infinite
 *   • Mobile (<768px):   30s linear infinite (CSS controlled via duration var
 *                       fallback; we pass a fixed value via inline style for
 *                       simplicity and let `prefers-reduced-motion` neutralise
 *                       it entirely.)
 *
 * Accessibility:
 *   • Wrapped in `aria-hidden="true"` because the content is decorative copy
 *     that repeats visually; screen readers don't need it on every page.
 *   • Pause-on-hover via `.sticky-marquee-wrap:hover .sticky-marquee-track`.
 *   • `prefers-reduced-motion: reduce` disables the animation (handled by the
 *     global CSS block at the bottom of index.css).
 *
 * Props:
 *   • bottomMarquee?: { items?: string[]; visible?: boolean }
 *
 * Behaviour:
 *   • If `bottomMarquee.visible === false` → renders nothing.
 *   • If items array is missing or empty → falls back to a sensible default so
 *     the page never breaks during loading or admin-misconfiguration.
 */

const DEFAULT_ITEMS = [
  'SEI CON \u2022 JULY 7\u20139, 2026',
  'SPORTS ENTERTAINMENT INNOVATION CONFERENCE',
  'SPORTS \u2022 ENTERTAINMENT \u2022 INNOVATION',
  'EDUCATION COLLABORATION DEALMAKING',
];

// Height tokens — kept in one place so the spacer in pages can mirror these.
// Desktop: 44px (h-11). Mobile: 36px (h-9). Tailwind responsive classes used below.
export const STICKY_MARQUEE_HEIGHT_CLASS = 'h-9 md:h-11';
// Matching padding-bottom for page wrappers to ensure no content overlap.
export const STICKY_MARQUEE_SPACER_CLASS = 'pb-9 md:pb-11';

export default function StickyBottomMarquee({ bottomMarquee }) {
  const visible = bottomMarquee?.visible !== false; // default true if undef
  const rawItems = bottomMarquee?.items;
  const items = useMemo(() => {
    if (Array.isArray(rawItems) && rawItems.length > 0) {
      const cleaned = rawItems.map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean);
      if (cleaned.length > 0) return cleaned;
    }
    return DEFAULT_ITEMS;
  }, [rawItems]);

  if (!visible) return null;

  // Repeat the items array this many times inside the track. Must stay in
  // sync with the `-25%` end-translate in `@keyframes stickyMarqueeScroll`
  // (-100% / REPEATS). One items-iteration ≈ 1377 px at desktop font;
  // 4× ≈ 5508 px which safely exceeds 3440 px ultrawide displays.
  const REPEATS = 4;

  const renderCells = () =>
    Array.from({ length: REPEATS }, (_, rep) =>
      items.map((text, i) => (
        <React.Fragment key={`r${rep}-${i}`}>
          <span
            className={[
              'shrink-0 px-6 font-body font-medium uppercase tracking-widest',
              'text-[11px] md:text-[13px] whitespace-nowrap',
              i === 0 ? 'text-brand-orange' : 'text-white',
            ].join(' ')}
            // Only the first repetition carries the `data-testid` so each item
            // identifier remains unique (duplicates are decorative).
            data-testid={rep === 0 ? `sticky-marquee-item-${i}` : undefined}
          >
            {text}
          </span>
          <span
            className="shrink-0 px-3 text-brand-orange text-[11px] md:text-[13px] leading-none select-none"
            aria-hidden="true"
          >
            {'\u25C6'}
          </span>
        </React.Fragment>
      ))
    );

  return (
    <div
      className={[
        'sticky-marquee-wrap',
        'fixed inset-x-0 bottom-0 z-40',
        'overflow-hidden',
        'bg-card-dark',
        'border-t border-brand-orange/40',
        STICKY_MARQUEE_HEIGHT_CLASS,
        'flex items-center',
        // Block pointer events for inner text but allow nothing else (decorative).
        'pointer-events-none select-none',
      ].join(' ')}
      style={{ '--sticky-marquee-duration': '40s' }}
      role="presentation"
      aria-hidden="true"
      data-testid="sticky-bottom-marquee"
    >
      <div className="sticky-marquee-track flex items-center w-max">
        {renderCells()}
      </div>
    </div>
  );
}
